import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { endOfMonth, format, parseISO, startOfMonth } from 'date-fns';
import {
  AlertTriangle,
  FileSearch,
  GraduationCap,
  Receipt,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

import { type ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

/**
 * A camada de dados do calendário operacional.
 *
 * O protótipo fazia CINCO consultas direto no Supabase pelo navegador (dias
 * letivos, vistorias, veículos, termos, prazos legais) e juntava tudo no
 * cliente. Aqui o front não fala com o banco (docs/ARQUITETURA.md, secao 1) —
 * essa junção passa a ser trabalho da API, numa única rota.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend. `apps/api`
 * ainda não tem módulo de calendário — a rota abaixo genuinamente não existe.
 * Decisão do dono de 2026-08-26 (CLAUDE.md, "Proibido", exceção estendida)
 * para demonstração ao cliente — mesmas três condições dos cinco módulos de
 * cadastro: só leitura (esta tela não tem ação de gravação), dado claramente
 * fictício, removido antes de `develop` → `hml` (`git grep -n "TEMPORÁRIO"
 * apps/web/src`, checklist em `docs/ALVOS.md`). Os eventos são gerados a
 * partir de hoje e filtrados para o mês pedido — trocar de mês no seletor
 * mostra períodos vazios de verdade quando não há evento simulado ali, que é
 * o comportamento honesto (nem todo mês tem evento).
 */

export type EventoTipo = 'letivo' | 'vistoria' | 'vencimento_doc' | 'termo' | 'prazo_legal';

export interface EventoCalendario {
  id: string;
  /** `YYYY-MM-DD`, como a API devolve. */
  data: string;
  titulo: string;
  tipo: EventoTipo;
  detalhe?: string;
  severidade?: 'info' | 'warning' | 'destructive';
}

interface TipoMeta {
  label: string;
  icon: LucideIcon;
  tone: string;
}

/** Rótulo, ícone e cor semântica de cada tipo de evento — só exibição. */
export const TIPO_META: Record<EventoTipo, TipoMeta> = {
  letivo: { label: 'Período letivo', icon: GraduationCap, tone: 'bg-primary/10 text-primary' },
  vistoria: { label: 'Vistoria', icon: Wrench, tone: 'bg-warning/10 text-warning' },
  vencimento_doc: {
    label: 'Vencimento de documento',
    icon: AlertTriangle,
    tone: 'bg-destructive/10 text-destructive',
  },
  termo: {
    label: 'Termo / Aditivo',
    icon: FileSearch,
    tone: 'bg-secondary text-secondary-foreground',
  },
  prazo_legal: { label: 'Prazo legal', icon: Receipt, tone: 'bg-muted text-foreground' },
};

export const CHAVES_DO_CALENDARIO = {
  eventos: (mes: string) => ['operacao', 'calendario', mes] as const,
};

function diasAPartirDeHoje(dias: number): string {
  const data = new Date();
  data.setDate(data.getDate() + dias);
  return data.toISOString().slice(0, 10);
}

/**
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 * Um evento de cada tipo, espalhados perto de hoje, cobrindo as três
 * severidades (info/warning/destructive) que a tela colore.
 */
function eventosDoCalendarioMock(): EventoCalendario[] {
  return [
    {
      id: 'evento-exemplo-letivo',
      data: diasAPartirDeHoje(2),
      titulo: 'Dia letivo',
      tipo: 'letivo',
      severidade: 'info',
    },
    {
      id: 'evento-exemplo-vistoria',
      data: diasAPartirDeHoje(5),
      titulo: 'Vistoria · ABC1D23',
      tipo: 'vistoria',
      detalhe: 'Status: AGENDADA',
      severidade: 'warning',
    },
    {
      id: 'evento-exemplo-vencimento-crlv',
      data: diasAPartirDeHoje(-3),
      titulo: 'CRLV vence · DEF2E34',
      tipo: 'vencimento_doc',
      severidade: 'destructive',
    },
    {
      id: 'evento-exemplo-vencimento-seguro',
      data: diasAPartirDeHoje(12),
      titulo: 'Seguro vence · GHI3F45',
      tipo: 'vencimento_doc',
      severidade: 'warning',
    },
    {
      id: 'evento-exemplo-termo',
      data: diasAPartirDeHoje(20),
      titulo: 'Fim de vigência · Termo Exemplo 01',
      tipo: 'termo',
      severidade: 'warning',
    },
    {
      id: 'evento-exemplo-prazo-legal',
      data: diasAPartirDeHoje(9),
      titulo: 'Prazo de recurso administrativo',
      tipo: 'prazo_legal',
      detalhe: 'Prazo exemplo para fins de demonstração.',
      severidade: 'info',
    },
  ];
}

/**
 * Os eventos operacionais do mês informado (`YYYY-MM`).
 *
 * Calcula início e fim do mês em `YYYY-MM-DD` — ver o comentário do topo do
 * arquivo sobre o mock e por que meses sem evento aparecem vazios de verdade.
 */
export function useEventosDoCalendario(mes: string): UseQueryResult<EventoCalendario[], ErroApi> {
  const inicioIso = format(startOfMonth(parseISO(`${mes}-01`)), 'yyyy-MM-dd');
  const fimIso = format(endOfMonth(parseISO(`${mes}-01`)), 'yyyy-MM-dd');

  return useQuery<EventoCalendario[], ErroApi>({
    queryKey: CHAVES_DO_CALENDARIO.eventos(mes),
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () =>
      eventosDoCalendarioMock().filter(
        (evento) => evento.data >= inicioIso && evento.data <= fimIso,
      ),
    retry: naoInsistirEmRecusa,
  });
}
